// @owners { team: patient-health }
import { v4 as uuid } from 'uuid';
import { getPendingTakenEvents } from '~shared/features/med-reminders/selectors/getMedicationTakenEvents';
import { get, post } from '~shared/helpers/apiHelper';
import { type APIError, type MedicationTakenEvent, type ReduxDispatch, type ReduxStateShared } from '~shared/types';
import {
  type MedicationTakenEventParts,
  type PendingMedicationTakenEvent,
  type SyncMedicationTakenEventSucceededPayload,
} from '~shared/types/medReminders';

export const SYNC_TAKEN_EVENTS_LOADING = 'SYNC_TAKEN_EVENTS_LOADING';
export type ActionSyncingTakenEvents = {
  type: typeof SYNC_TAKEN_EVENTS_LOADING;
};
export function syncingTakenEvents(): ActionSyncingTakenEvents {
  return {
    type: SYNC_TAKEN_EVENTS_LOADING,
  };
}

export const SYNC_TAKEN_EVENTS_SUCCEEDED = 'SYNC_TAKEN_EVENTS_SUCCEEDED';
export type ActionSyncTakenEventsSucceeded = {
  type: typeof SYNC_TAKEN_EVENTS_SUCCEEDED;
  payload: SyncMedicationTakenEventSucceededPayload;
};
export function syncTakenEventsSucceeded(
  payload: SyncMedicationTakenEventSucceededPayload,
): ActionSyncTakenEventsSucceeded {
  return {
    type: SYNC_TAKEN_EVENTS_SUCCEEDED,
    payload,
  };
}

export const SYNC_TAKEN_EVENTS_FAILED = 'SYNC_TAKEN_EVENTS_FAILED';
export type ActionSyncTakenEventsFailed = {
  type: typeof SYNC_TAKEN_EVENTS_FAILED;
  payload: APIError;
  error: boolean;
};
export function syncTakenEventsFailed(error: APIError): ActionSyncTakenEventsFailed {
  return {
    type: SYNC_TAKEN_EVENTS_FAILED,
    payload: error,
    error: true,
  };
}

export const FETCH_TAKEN_EVENTS_LOADING = 'FETCH_TAKEN_EVENTS_LOADING';
export type ActionFetchingTakenEvents = {
  type: typeof FETCH_TAKEN_EVENTS_LOADING;
};
export function fetchingTakenEvents(): ActionFetchingTakenEvents {
  return {
    type: FETCH_TAKEN_EVENTS_LOADING,
  };
}

export const FETCH_TAKEN_EVENTS_SUCCEEDED = 'FETCH_TAKEN_EVENTS_SUCCEEDED';
export type ActionFetchTakenEventsSucceeded = {
  type: typeof FETCH_TAKEN_EVENTS_SUCCEEDED;
  payload: MedicationTakenEvent[];
};
export function fetchTakenEventsSucceeded(events: MedicationTakenEvent[]): ActionFetchTakenEventsSucceeded {
  return {
    type: FETCH_TAKEN_EVENTS_SUCCEEDED,
    payload: events,
  };
}

export const FETCH_TAKEN_EVENTS_FAILED = 'FETCH_TAKEN_EVENTS_FAILED';
export type ActionFetchTakenEventsFailed = {
  type: typeof FETCH_TAKEN_EVENTS_FAILED;
  payload: APIError;
  error: boolean;
};
export function fetchTakenEventsFailed(error: APIError): ActionFetchTakenEventsFailed {
  return {
    type: FETCH_TAKEN_EVENTS_FAILED,
    payload: error,
    error: true,
  };
}

export const ADD_TAKEN_EVENT = 'ADD_TAKEN_EVENT';
export type ActionAddTakenEvent = {
  type: typeof ADD_TAKEN_EVENT;
  payload: PendingMedicationTakenEvent;
};

export function createTakenEvent(takenEvent: MedicationTakenEventParts): ActionAddTakenEvent {
  const event = { ...takenEvent, uuid: uuid(), deleted_at: null };
  return {
    type: ADD_TAKEN_EVENT,
    payload: event,
  };
}

export function deleteTakenEvent(takenEvent: MedicationTakenEventParts): ActionAddTakenEvent {
  const event = { ...takenEvent, uuid: uuid(), deleted_at: new Date().toString() };
  return {
    type: ADD_TAKEN_EVENT,
    payload: event,
  };
}

/**
 * @deprecated Redux networking action.
 * @see https://www.notion.so/alto/Guidebook-Migrating-Redux-Networking-Actions-To-React-Query-8567e05fc96c46fcb8020595f24b0edc
 */
export function syncTakenEvents() {
  return (dispatch: ReduxDispatch, getState: () => ReduxStateShared) => {
    dispatch(syncingTakenEvents());

    const state = getState();
    const takenEvents = getPendingTakenEvents(state);
    return post('/medication_taken_events/sync', {
      medication_taken_events: takenEvents,
    }).then((json) => {
      if (json.error) {
        dispatch(syncTakenEventsFailed(json));
        return false;
      }

      dispatch(syncTakenEventsSucceeded(json));
      return true;
    });
  };
}

/**
 * @deprecated Redux networking action.
 * @see https://www.notion.so/alto/Guidebook-Migrating-Redux-Networking-Actions-To-React-Query-8567e05fc96c46fcb8020595f24b0edc
 */
export function fetchTakenEvents() {
  return (dispatch: ReduxDispatch) => {
    dispatch(fetchingTakenEvents());

    return get('/medication_taken_events').then((json) => {
      if (json.error) {
        dispatch(fetchTakenEventsFailed(json.error));
        return false;
      }

      dispatch(fetchTakenEventsSucceeded(json));
      return true;
    });
  };
}

export type MedicationTakenEventAction =
  | ActionAddTakenEvent
  | ActionSyncingTakenEvents
  | ActionSyncTakenEventsFailed
  | ActionSyncTakenEventsSucceeded
  | ActionFetchingTakenEvents
  | ActionFetchTakenEventsFailed
  | ActionFetchTakenEventsSucceeded;
